import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { Camera, X as XIcon } from "lucide-react";
import { Card } from "@/components/admin/ui";

/*
 * ScannerModal — camera QR/barcode scanner overlay.
 *
 * Root-cause of previous "not reading" bug:
 *   React.StrictMode double-invokes effects in dev builds.
 *   The first scanner start set active=true; StrictMode cleanup
 *   immediately set active=false and tried to stop a not-yet-started
 *   scanner; the second start attempt hit "already running" and failed.
 *   Every successful scan was suppressed by the stale `active` flag.
 *
 * Fix:
 *   1. `onResult` is stored in a ref — the effect only depends on `[open]`.
 *      This prevents unnecessary scanner restarts when the parent
 *      re-creates the callback.
 *   2. A 150 ms setTimeout lets the StrictMode first-mount cleanup
 *      finish before the real start attempt.  If cleanup fires again
 *      (StrictMode second unmount) it clears the timer and calls the
 *      stop helper — so the scanner is never started twice.
 *   3. `getState()` is used for accurate lifecycle checks instead of the
 *      brittle `isScanning` boolean property.
 */

const SCANNER_ELEMENT_ID = "egr-qr-scanner-region";

const stopScanner = async (scanner) => {
  if (!scanner) return;
  try {
    const state = scanner.getState();
    if (
      state === Html5QrcodeScannerState.SCANNING ||
      state === Html5QrcodeScannerState.PAUSED
    ) {
      await scanner.stop();
    }
    await scanner.clear();
  } catch (e) {
    console.warn("[ScannerModal] cleanup:", e?.message);
  }
};

export const ScannerModal = ({ open, onClose, onResult, hint }) => {
  const readerRef = useRef(null);
  const scannerRef = useRef(null);
  const onResultRef = useRef(onResult);
  const [err, setErr] = useState("");

  // Keep the callback ref fresh without triggering effect re-runs.
  useLayoutEffect(() => {
    onResultRef.current = onResult;
  });

  useEffect(() => {
    if (!open) {
      const s = scannerRef.current;
      scannerRef.current = null;
      stopScanner(s);
      return;
    }

    setErr("");
    let cancelled = false;
    let scanner = null;

    // Defer start so React StrictMode's immediate cleanup can settle.
    const timer = setTimeout(async () => {
      if (cancelled) return;
      const el = readerRef.current;
      if (!el) return;

      try {
        scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 12, qrbox: { width: 250, height: 250 } },
          (decoded) => {
            if (!cancelled) onResultRef.current(decoded);
          },
          () => {} // per-frame "not found" — suppress console noise
        );
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Camera unavailable. Check browser permissions.");
      }
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      const s = scannerRef.current;
      scannerRef.current = null;
      stopScanner(s);
    };
  }, [open]); // intentionally omits onResult — handled via ref above

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="scanner-modal"
    >
      <Card className="p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#FFD166]" />
            <span className="eyebrow">Scan Barcode / QR</span>
          </div>
          <button
            onClick={onClose}
            className="text-[#B9C8D5] hover:text-[#FFD166]"
            data-testid="scanner-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Scanner viewport */}
        <div
          id={SCANNER_ELEMENT_ID}
          ref={readerRef}
          className="rounded-xl overflow-hidden bg-black w-full"
          style={{ aspectRatio: "1 / 1", minHeight: 240 }}
        />

        {err && (
          <div className="text-sm text-red-400 mt-3" data-testid="scanner-error">
            {err}
          </div>
        )}

        <p className="text-xs text-[#B9C8D5] mt-3 text-center">
          {hint || "Point your camera at an EGR barcode. Detection is automatic."}
        </p>
      </Card>
    </div>
  );
};
